import { createElement, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

type SidebarContextMenuProps = {
  visible: boolean;
  x: number;
  y: number;
  onClose: () => void;
  onDuplicate: () => void;
  onRename: () => void;
  onTrash: () => void;
  onToggleFavorite?: () => void;
  onTogglePrivate?: () => void;
  onCopyMarkdown?: () => void;
  onExportMarkdown?: () => void;
  isFavorite?: boolean;
  isPrivate?: boolean;
  canTrash?: boolean;
  canDuplicate?: boolean;
  canRename?: boolean;
  canFavorite?: boolean;
  canExportMarkdown?: boolean;
};

const MENU_WIDTH = 228;
const MENU_HEIGHT_ESTIMATE = 240;

function MenuItem({
  label,
  onPress,
  destructive,
}: {
  label: string;
  onPress: () => void;
  destructive?: boolean;
}) {
  return createElement(
    'button',
    {
      type: 'button',
      className: `w-full px-3 py-1.5 text-left text-sm hover:bg-surface-raised ${
        destructive ? 'text-danger' : 'text-fg'
      }`,
      onClick: (event: MouseEvent) => {
        event.stopPropagation();
        onPress();
      },
    },
    label,
  );
}

function eventElement(target: EventTarget | null): Element | null {
  if (target instanceof Element) {
    return target;
  }
  if (target instanceof Node) {
    return target.parentElement;
  }
  return null;
}

function ToggleRow({
  label,
  checked,
  title,
  onToggle,
}: {
  label: string;
  checked: boolean;
  title?: string;
  onToggle: () => void;
}) {
  function activate(event: MouseEvent): void {
    if (event.type === 'mousedown' && event.button !== 0) {
      return;
    }
    if (event.type === 'click' && event.detail !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    onToggle();
  }

  return createElement(
    'button',
    {
      type: 'button',
      role: 'menuitemcheckbox',
      'aria-checked': checked,
      title,
      className:
        'flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm text-fg hover:bg-surface-raised',
      onMouseDown: activate,
      onClick: activate,
    },
    createElement('span', { className: 'pointer-events-none' }, label),
    createElement(
      'span',
      {
        'aria-hidden': true,
        className: `pointer-events-none relative inline-block h-[18px] w-8 shrink-0 rounded-full ${
          checked ? 'bg-accent' : 'bg-border'
        }`,
      },
      createElement('span', {
        className: `absolute left-0.5 top-0.5 h-3.5 w-3.5 rounded-full bg-surface shadow-sm transition-transform ${
          checked ? 'translate-x-3.5' : ''
        }`,
      }),
    ),
  );
}

function clampPosition(x: number, y: number): { x: number; y: number } {
  if (typeof window === 'undefined') {
    return { x, y };
  }
  const maxX = window.innerWidth - MENU_WIDTH - 8;
  const maxY = window.innerHeight - MENU_HEIGHT_ESTIMATE - 8;
  return {
    x: Math.max(8, Math.min(x, maxX)),
    y: Math.max(8, Math.min(y, maxY)),
  };
}

export function SidebarContextMenu({
  visible,
  x,
  y,
  onClose,
  onDuplicate,
  onRename,
  onTrash,
  onToggleFavorite,
  onTogglePrivate,
  onCopyMarkdown,
  onExportMarkdown,
  isFavorite = false,
  isPrivate = false,
  canTrash = true,
  canDuplicate = true,
  canRename = true,
  canFavorite = false,
  canExportMarkdown = false,
}: SidebarContextMenuProps) {
  const [position, setPosition] = useState(() => clampPosition(x, y));

  useLayoutEffect(() => {
    if (visible) {
      setPosition(clampPosition(x, y));
    }
  }, [visible, x, y]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    function handlePointerDown(event: MouseEvent): void {
      const target = eventElement(event.target);
      if (target?.closest('[data-sidebar-menu="true"]')) {
        return;
      }
      onClose();
    }
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        onClose();
      }
    }
    // Click, not mousedown: a mousedown dismiss unmounts the row before its action runs.
    document.addEventListener('click', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, onClose]);

  if (!visible || typeof document === 'undefined') {
    return null;
  }

  const menu = createElement(
    'div',
    {
      'data-sidebar-menu': 'true',
      className:
        'fixed min-w-[228px] overflow-hidden rounded-lg border border-border bg-surface-overlay py-1 shadow-xl',
      style: { left: position.x, top: position.y, zIndex: 99999 },
      onMouseDown: (event: MouseEvent) => event.stopPropagation(),
      onContextMenu: (event: MouseEvent) => event.preventDefault(),
    },
    canDuplicate
      ? MenuItem({
          label: 'Duplicate',
          onPress: () => {
            onClose();
            onDuplicate();
          },
        })
      : null,
    canRename
      ? MenuItem({
          label: 'Rename',
          onPress: () => {
            onClose();
            onRename();
          },
        })
      : null,
    canFavorite && onToggleFavorite
      ? ToggleRow({
          label: 'Favorite',
          checked: isFavorite,
          onToggle: onToggleFavorite,
        })
      : null,
    canFavorite && onTogglePrivate
      ? ToggleRow({
          label: 'Private',
          checked: isPrivate,
          title: 'Hidden from assistants',
          onToggle: onTogglePrivate,
        })
      : null,
    canExportMarkdown && onCopyMarkdown
      ? MenuItem({
          label: 'Copy as Markdown',
          onPress: () => {
            onClose();
            onCopyMarkdown();
          },
        })
      : null,
    canExportMarkdown && onExportMarkdown
      ? MenuItem({
          label: 'Export Markdown',
          onPress: () => {
            onClose();
            onExportMarkdown();
          },
        })
      : null,
    canTrash
      ? createElement('div', { className: 'my-1 border-t border-border' })
      : null,
    canTrash
      ? MenuItem({
          label: 'Move to Trash',
          destructive: true,
          onPress: () => {
            onClose();
            onTrash();
          },
        })
      : null,
  );

  return createPortal(menu, document.body);
}
